/*
 * NOTICE OF LICENSE
 *
 * This source file is subject to the Academic Free License (AFL 3.0)
 *
 *  @author    Afternet <info@afternet.gr>
 *  @copyright 2022 Afternet
 *  @license   http://opensource.org/licenses/afl-3.0.php  Academic Free License (AFL 3.0)
 */

import React from 'react';
import {useParams} from "react-router-dom";

function generateItemList(parts, params, carPagejsonLdTitle, carPagejsonLdDescription) {
    const itemListElement = [];
    let position = 1;

    parts.forEach(category => {
        category.subcategories?.forEach(subcat => {
            subcat.parts?.forEach(part => {
                // determine image: prefer part.part_image, otherwise use first image from part.images when total_images >= 1
                const totalImages = Number(part?.total_images || 0);
                const firstImage = Array.isArray(part?.images) && part.images.length ? part.images[0].image : undefined;
                const image = part?.part_image || (totalImages >= 1 ? firstImage : undefined);

                const itemUrl = `${process.env.REACT_APP_SITE_URL || 'https://autorecparts.gr/'}${params.lang}/used-part/${part.slug}/${params.make}/${params.model}/${params.year}/${params.city}/${part.code}`;

                // build item without image and without price by default; add image/offers only when present
                const item = {
                    "@type": "ListItem",
                    "position": position++,
                    "url": itemUrl,
                    "name": part.title,
                    "sku": part.code,
                    "itemCondition": "https://schema.org/UsedCondition"
                };

                // Only add the image property when image is not null (so null won't be serialized into JSON)
                if (image != null) {
                    item.image = image;
                }

                // Add offers only when part.price exists and numeric value is greater than 0
                const priceStr = part?.price != null ? String(part.price).trim() : '';
                const priceNum = priceStr === '' ? NaN : parseFloat(priceStr.replace(',', '.'));
                const hasPrice = !isNaN(priceNum) && priceNum > 0;

                if (hasPrice) {
                    // ensure price has two decimals
                    const priceFixed = priceNum.toFixed(2);
                    item.offers = {
                        "@type": "Offer",
                        "price": priceFixed,
                        "priceCurrency": "EUR",
                        "url": itemUrl,
                        "availability": "https://schema.org/InStock"
                    };
                }

                itemListElement.push(item);
            });
        });
    });

    return {
        "@type": "ItemList",
        "name": carPagejsonLdTitle,
        "description": carPagejsonLdDescription,
        "itemListElement": itemListElement
    };
}


const JsonLdCarProduct = ({
    name,
    carPagejsonLdTitle,
    description,
    carPagejsonLdDescription,
    url,
    items = [],
    publisher,
    datePublished,
    inLanguage,
}) => {

    let params = useParams();

    const resolvedUrl = url || (typeof window !== 'undefined' ? window.location.href : undefined);

    const partsList = generateItemList(items , params, carPagejsonLdTitle, carPagejsonLdDescription);

    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name,
        description,
        url: resolvedUrl,
        datePublished,
        inLanguage,
        publisher:
            publisher && typeof publisher === 'string'
                ? { '@type': 'Organization', name: publisher }
                : publisher,
        mainEntity: items && items.length ? partsList : undefined,
    };

    // remove undefined fields and empty arrays
    const cleaned = JSON.parse(JSON.stringify(jsonLd));

    return (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(cleaned) }}
        />
    );
};

export default JsonLdCarProduct;
